import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckIcon, ChevronLeftIcon } from '@heroicons/react/24/outline';
import type { TaskBodyProps } from '../index';
import { fetchPatientCard } from '../../../../services/patientCard';
import { advanceWorkflow } from '../../../../services/workQueue';
import { useToast } from '../../../../providers/ToastProvider';
import { apiErrorMessage } from '../../../../utils/apiError';
import { SPARK_PHOTO_SLOTS } from '../../../../services/sparkSubmission';
import {
    CARD_STALE_MS,
    MIN_SPARK_PHOTOS,
    STEPS,
    type FooterAction,
    type RegisterFooter,
    type RegisterBusy,
    type StepId
} from './spark.shared';
import { CreateCaseStep } from './CreateCaseStep';
import { UploadPhotosStep } from './UploadPhotosStep';
import { UploadScansStep } from './UploadScansStep';

export function SparkSubmissionWizard({
    workCase: c,
    statusMeta,
    onPayloadChange,
    onTaskComplete
}: TaskBodyProps) {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    // Passing the completed action to onTaskComplete is what makes the surface
    // show the completion card instead of silently closing back to the queue.
    const forwardAction = statusMeta.outgoingActions.find(
        (a) => a.action === 'create-in-manufacturer'
    );

    const { data: card } = useQuery({
        queryKey: ['patient-card', c.id],
        queryFn: () => fetchPatientCard(c.id),
        staleTime: CARD_STALE_MS,
        enabled: Boolean(c.id)
    });

    const [stepIndex, setStepIndex] = useState(0);
    const [caseId, setCaseId] = useState<string | null>(null);
    const [link, setLink] = useState<string | null>(null);
    const [caseIsNew, setCaseIsNew] = useState(false);
    const [photosUploaded, setPhotosUploaded] = useState(false);
    const [scansUploaded, setScansUploaded] = useState(false);
    const [advancing, setAdvancing] = useState(false);
    // The active step raises this while an upload is in flight; it (plus the
    // advance call) locks all navigation so files can't be interrupted mid-send.
    const [stepBusy, setStepBusy] = useState(false);
    const [footer, setFooter] = useState<FooterAction | null>(null);
    const [photosProgress, setPhotosProgress] = useState<{ placed: number; total: number } | null>(
        null
    );
    // Photo/scan selections live here (not in the step components) so they survive
    // step navigation - a step unmounts when you leave it.
    const [photoAssignments, setPhotoAssignments] = useState<Record<string, string>>({});
    const photosPrefilledRef = useRef(false);
    // Signature of the photo set last uploaded to Spark - lets us skip a re-upload
    // when returning to the step with the same photos.
    const [uploadedPhotosSig, setUploadedPhotosSig] = useState<string | null>(null);
    const [scanSelected, setScanSelected] = useState<Record<string, boolean>>({});

    // Stable registrars: the active step pushes its footer action / progress up.
    const registerFooter = useCallback<RegisterFooter>((action) => setFooter(action), []);
    const registerPhotosProgress = useCallback(
        (progress: { placed: number; total: number } | null) => setPhotosProgress(progress),
        []
    );
    const registerBusy = useCallback<RegisterBusy>((busy) => setStepBusy(busy), []);

    // True while an upload (or the follow-up advance) is in flight - gates every
    // navigation control so a transfer can't be interrupted partway through.
    const locked = stepBusy || advancing;

    // This task drives its own completion - hide TaskSurface's footer. Also relay
    // `locked` so the surface disables its "back to queue" control during uploads.
    useEffect(() => {
        onPayloadChange?.({ payload: null, valid: false, bodyOwnsSubmit: true, busy: locked });
    }, [onPayloadChange, locked]);

    // Prefill from persisted refs (returning to a partially-completed case).
    useEffect(() => {
        if (!card) return;
        setCaseId((prev) => prev ?? (card.treatment?.manufacturerCaseId || null));
        setLink((prev) => prev ?? (card.treatment?.manufacturerLink || null));
    }, [card]);

    const currentStep = STEPS[stepIndex];

    const stepDone = useMemo<Record<StepId, boolean>>(
        () => ({
            case: Boolean(caseId),
            refinement: false,
            photos: photosUploaded,
            scans: scansUploaded
        }),
        [caseId, photosUploaded, scansUploaded]
    );

    const goBack = useCallback(() => setStepIndex((i) => Math.max(i - 1, 0)), []);

    // Step 1 → store the case refs and advance in one move.
    const handleCaseReady = useCallback((id: string, url: string, isNew = false) => {
        setCaseId(id);
        setLink(url);
        setCaseIsNew(isNew);
        setStepIndex(1);
    }, []);

    // Step 1 (undo) → case was detached; reset the wizard back to the start.
    const handleCaseUnlinked = useCallback(() => {
        setCaseId(null);
        setLink(null);
        setCaseIsNew(false);
        setPhotosUploaded(false);
        setScansUploaded(false);
        setPhotoAssignments({});
        photosPrefilledRef.current = false;
        setUploadedPhotosSig(null);
        setScanSelected({});
        setStepIndex(0);
        // Refresh the card so the prefill effect doesn't re-populate stale refs.
        void queryClient.invalidateQueries({ queryKey: ['patient-card', c.id] });
    }, [queryClient, c.id]);

    const handlePhotosContinue = useCallback((uploadedSig: string) => {
        setPhotosUploaded(true);
        setUploadedPhotosSig(uploadedSig);
        setStepIndex(2);
    }, []);

    // Returns true once the case has advanced - the scans step keys its success
    // toast off this so the toast lands right before the redirect, not before the
    // (network-bound) advance call.
    const handleFinish = useCallback(async (): Promise<boolean> => {
        if (!caseId || !link) return false;
        setAdvancing(true);
        try {
            await advanceWorkflow(c.id, 'create-in-manufacturer', {
                manufacturerCaseId: caseId,
                manufacturerLink: link
            });
            setScansUploaded(true);
            onTaskComplete?.(forwardAction);
            return true;
        } catch (err) {
            showToast(
                apiErrorMessage(err, 'Failed to advance the case. Please try again.'),
                'error'
            );
            return false;
        } finally {
            setAdvancing(false);
        }
    }, [c.id, caseId, link, forwardAction, onTaskComplete, showToast]);

    return (
        <div className="flex-1 min-h-0 flex flex-col">
            {/*  Stepper  */}
            <div className="flex items-center justify-center gap-2 px-5 py-4 border-b border-gray-200 flex-shrink-0">
                {STEPS.map((step, index) => {
                    const Icon = step.icon;
                    const isActive = index === stepIndex;
                    const isPast = index < stepIndex;
                    const reachable =
                        index <= stepIndex || STEPS.slice(0, index).every((s) => stepDone[s.id]);
                    return (
                        <div key={step.id} className="flex items-center">
                            <button
                                type="button"
                                disabled={!reachable || locked}
                                onClick={() => reachable && !locked && setStepIndex(index)}
                                aria-current={isActive ? 'step' : undefined}
                                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-400 ${
                                    isActive
                                        ? 'bg-gray-900 text-white cursor-pointer'
                                        : isPast
                                          ? 'bg-gray-100 text-gray-700 hover:bg-gray-200 cursor-pointer'
                                          : reachable
                                            ? 'bg-gray-50 text-gray-500 hover:bg-gray-100 hover:text-gray-700 cursor-pointer'
                                            : 'bg-gray-50 text-gray-300 cursor-not-allowed'
                                }`}
                            >
                                <Icon className="w-3.5 h-3.5" />
                                <span>{step.label}</span>
                                {stepDone[step.id] && (
                                    <CheckIcon className="w-3.5 h-3.5 text-green-500" />
                                )}
                            </button>
                            {index < STEPS.length - 1 && (
                                <div
                                    className={`w-6 h-px mx-1 ${isPast ? 'bg-gray-300' : 'bg-gray-200'}`}
                                />
                            )}
                        </div>
                    );
                })}
            </div>

            {/*  Photos sub-header - fixed above the scrolling slot grid.  */}
            {currentStep.id === 'photos' &&
                (() => {
                    const placed = photosProgress?.placed ?? 0;
                    const total = photosProgress?.total ?? SPARK_PHOTO_SLOTS.length;
                    const pct = total > 0 ? Math.round((placed / total) * 100) : 0;
                    const meetsMinimum = placed >= MIN_SPARK_PHOTOS;
                    return (
                        <div className="flex items-start justify-between gap-4 px-6 py-3 border-b border-gray-200 bg-white flex-shrink-0">
                            <p className="text-sm text-gray-600 min-w-0">
                                Place the patient's photos into their positions. Slots auto-fill
                                when a photo already has a known position.
                            </p>
                            <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                <div className="flex items-center gap-2.5">
                                    <span className="text-xs font-semibold text-gray-600 whitespace-nowrap">
                                        {placed} of {total} placed
                                    </span>
                                    <div className="w-28 h-1.5 rounded-full bg-gray-200 overflow-hidden">
                                        <div
                                            className={`h-full rounded-full transition-all duration-200 ${meetsMinimum ? 'bg-green-500' : 'bg-gray-900'}`}
                                            style={{ width: `${pct}%` }}
                                        />
                                    </div>
                                </div>
                                <span
                                    className={`text-[11px] whitespace-nowrap ${meetsMinimum ? 'text-green-600' : 'text-gray-400'}`}
                                >
                                    {meetsMinimum
                                        ? `Minimum of ${MIN_SPARK_PHOTOS} photos met`
                                        : `At least ${MIN_SPARK_PHOTOS} photos required to continue`}
                                </span>
                            </div>
                        </div>
                    );
                })()}

            {/*  Body  */}
            <div className="flex-1 min-h-0 overflow-auto p-6">
                {currentStep.id === 'case' && (
                    <CreateCaseStep
                        patientId={c.id}
                        card={card}
                        caseId={caseId}
                        link={link}
                        isNew={caseIsNew}
                        onReady={handleCaseReady}
                        onUnlinked={handleCaseUnlinked}
                        registerFooter={registerFooter}
                    />
                )}
                {currentStep.id === 'photos' && (
                    <UploadPhotosStep
                        patientId={c.id}
                        onContinue={handlePhotosContinue}
                        registerFooter={registerFooter}
                        registerBusy={registerBusy}
                        registerPhotosProgress={registerPhotosProgress}
                        assignments={photoAssignments}
                        setAssignments={setPhotoAssignments}
                        prefilledRef={photosPrefilledRef}
                        uploadedSig={uploadedPhotosSig}
                    />
                )}
                {currentStep.id === 'scans' && (
                    <UploadScansStep
                        patientId={c.id}
                        advancing={advancing}
                        onFinish={handleFinish}
                        registerFooter={registerFooter}
                        registerBusy={registerBusy}
                        selected={scanSelected}
                        setSelected={setScanSelected}
                    />
                )}
            </div>

            {/*  Footer - Back (bottom-left) + the active step's primary action (bottom-right).  */}
            <div className="flex items-center justify-between px-5 py-3 border-t border-gray-200 flex-shrink-0">
                <button
                    type="button"
                    onClick={goBack}
                    disabled={stepIndex === 0 || locked}
                    className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    <ChevronLeftIcon className="w-4 h-4" />
                    Back
                </button>
                {footer ? (
                    <button
                        type="button"
                        onClick={footer.onClick}
                        disabled={footer.disabled}
                        className="flex items-center gap-2 bg-gray-900 text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {footer.label}
                    </button>
                ) : (
                    <span />
                )}
            </div>
        </div>
    );
}
